import { Injectable, inject } from '@angular/core';
import {FacetSortUtils} from '../shared/utils/facet-sort.utils';
import {
    ActiveFacet, FacetGroup, FacetGroupConfig,
    FacetGroupForInterface,
    FacetValueForInterface,
    MultiFacet
} from '../shared/interfaces/assets-search-results.interface';
import {TranslateService} from '@ngx-translate/core';
import {Discovery} from '../shared/configurations/discovery.constant';
import {
    FacetConfig
} from '../shared/interfaces/research-portal/portal-configuration/brief-results-configuration.interface';


@Injectable({
    providedIn: 'root',
})
export class FacetService {
    private translate = inject(TranslateService);


    facetSearchNameMapping = {'facet_tlevel': 'tlevel', 'esploro_asset_org': 'academic_unit', 'facet_creationdate': 'creationdate'};

    buildFacetConfiguration(facetsConfiguration: FacetConfig[]){
        const facetsConfigurationMap = new Map();
        facetsConfiguration.forEach(facet => {
            const facetGroupConfig = new FacetGroupConfig(facet.state, facet.valuesToDisplay, facet.sortType);
            let facetName = facet.code;
            if (this.facetSearchNameMapping[facetName]){
                facetName = this.facetSearchNameMapping[facetName];
            }
            facetsConfigurationMap.set(facetName, facetGroupConfig);
        });
        return facetsConfigurationMap;
    }



    buildFacetsForInterface(facets: FacetGroup[], activeFilters: string[],
                            facetsConfiguration: Map<string, FacetGroupConfig>, entity){
        if(entity === 'outputs') {
            return this.buildAssetFacetsForInterface(facets, activeFilters, facetsConfiguration);
        } else if (entity === 'researchers'){
            return this.buildResearchersFacetsForInterface(facets, activeFilters, facetsConfiguration);
        } else if (entity === 'projects'){
            return this.buildProjectFacetsForInterface(facets, activeFilters, facetsConfiguration);
        }
    }

    buildResearchersFacetsForInterface(facets: FacetGroup[], activeFilters: string[], facetsConfiguration: Map<string, FacetGroupConfig>){
        const facetsForInterface = [];
        this.sortFacetsGroupByConfigurationOrder(facets, facetsConfiguration);
        for (const facet of facets){
            const facetGroup = new FacetGroupForInterface(facet.name,
                this.translate.instant('research.profile.search.facets.' + facet.name));
            const facetValues: FacetValueForInterface[] = [];
            const values = facet.values.filter(value => this.removeActiveFacets(activeFilters, facet.name, value.value));
            if (facet.name === Discovery.INTERNAL_AFFILIATION_START_DATE_FACET){
                if (values.length >= 1){
                    const facetValue = new FacetValueForInterface(undefined, "", undefined);
                    facetValue.from = parseInt(values[0].value);
                    facetValue.to = values.length > 1? parseInt(values[values.length-1].value): parseInt(values[0].value);
                    facetValue.label = this.translate.instant('research.aria.search.facets.checkbox.creation.date', {from: facetValue.from, to: facetValue.to});
                    facetValues.push(facetValue);
                }
            } else {
                for (const value of values){
                    const label = this.getFacetValueLabel(facet.name, value.value);
                    const facetValue = new FacetValueForInterface(value.value, label, parseInt(value.count));
                    facetValues.push(facetValue);
                }
            }
            const facetGroupConfig = this.getConfigurationByFacetGroup(facetsConfiguration, facet);
            facetGroup.setExpanded(facetGroupConfig.state);
            facetGroup.setLimit(facetGroupConfig.valuesToDisplay);
            if (facetValues.length > 0){
                this.sortFacetsItemByConfigurationMethod(facetGroupConfig, facetValues);
                facetGroup.setItems(facetValues.slice(0,Discovery.RESEARCHER_FACETS_MAX_ITEMS_TO_DISPLAY));
                facetsForInterface.push(facetGroup);
            }
        }
        return facetsForInterface;
    }

    buildAssetFacetsForInterface(facets: FacetGroup[], activeFilters: string[], facetsConfiguration: Map<string, FacetGroupConfig>){
        const facetsForInterface = [];
        this.sortFacetsGroupByConfigurationOrder(facets, facetsConfiguration);
        for (const facet of facets){
            const facetGroup = new FacetGroupForInterface('facet_' + facet.name,
                this.translate.instant('research.facets.facet.facet_' + facet.name));
            const facetValues: FacetValueForInterface[] = [];
            const values = facet.values.filter(value => this.removeActiveFacets(activeFilters, 'facet_' + facet.name, value.value));

            if (facet.name === 'creationdate'){
                if (values.length >= 1){
                    const facetValue = new FacetValueForInterface(undefined, "", undefined);
                    facetValue.from = parseInt(values[0].value);
                    facetValue.to = values.length > 1? parseInt(values[1].value): parseInt(values[0].value);
                    facetValue.label = this.translate.instant('research.aria.search.facets.checkbox.creation.date', {from: facetValue.from, to: facetValue.to});
                    facetValues.push(facetValue);
                }
            } else {
                for (const value of values){
                    if (facet.name === 'tlevel' && value.value === 'online_resources'){
                        continue;
                    }
                    const label = this.getFacetValueLabel(facet.name, value.value);
                    const facetValue = new FacetValueForInterface(value.value, label, parseInt(value.count));
                    facetValues.push(facetValue);
                }
            }
            const facetGroupConfig = this.getConfigurationByFacetGroup(facetsConfiguration, facet);
            facetGroup.setExpanded(facetGroupConfig.state);
            facetGroup.setLimit(facetGroupConfig.valuesToDisplay);
            if (facetValues.length > 0){
                this.sortFacetsItemByConfigurationMethod(facetGroupConfig, facetValues);
                facetGroup.setItems(facetValues);
                facetsForInterface.push(facetGroup);
            }
        }
        return facetsForInterface;
    }

    buildProjectFacetsForInterface(facets: FacetGroup[], activeFilters: string[], facetsConfiguration: Map<string, FacetGroupConfig>){
        const facetsForInterface = [];
        this.sortFacetsGroupByConfigurationOrder(facets, facetsConfiguration);
        for (const facet of facets){
            const facetGroup = new FacetGroupForInterface(facet.name,
                this.translate.instant('research.projects.facet.' + facet.name));
            const facetValues: FacetValueForInterface[] = [];
            const values = facet.values.filter(value => this.removeActiveFacets(activeFilters, facet.name, value.value));

            for (const value of values){
                const label = this.getFacetValueLabel(facet.name, value.value, value.label);
                const facetValue = new FacetValueForInterface(value.value, label, parseInt(value.count));
                facetValues.push(facetValue);
            }
            const facetGroupConfig = this.getConfigurationByFacetGroup(facetsConfiguration, facet);
            facetGroup.setExpanded(facetGroupConfig.state);
            facetGroup.setLimit(facetGroupConfig.valuesToDisplay);
            if (facetValues.length > 0){
                this.sortFacetsItemByConfigurationMethod(facetGroupConfig, facetValues);
                facetGroup.setItems(facetValues);
                facetsForInterface.push(facetGroup);
            }
        }
        return facetsForInterface;
    }

    sortFacetsGroupByConfigurationOrder(facets: FacetGroup[], facetsConfiguration: Map<string, FacetGroupConfig>){
        const sortingFacets = Array.from( facetsConfiguration.keys() );
        facets.sort(function(a, b){
            return sortingFacets.indexOf(a.name) - sortingFacets.indexOf(b.name);
        });
    }

    sortFacetsItemByConfigurationMethod(facetGroupConfig: FacetGroupConfig, facetValues){
        if (facetGroupConfig.sortType === 'size'){
            facetValues.sort(FacetSortUtils.sortBySizeOrder);
        } else if (facetGroupConfig.sortType === 'alphnumeric'){
            facetValues.sort(FacetSortUtils.sortByAlphabeticalOrder);
        }
    }

    getConfigurationByFacetGroup(facetsConfiguration: Map<string, FacetGroupConfig>, facet: FacetGroup){
        if (facetsConfiguration.has(facet.name)){
            return facetsConfiguration.get(facet.name);
        }
        //return the OTB
        return new FacetGroupConfig('expanded', '5', 'size');
    }

    removeActiveFacets(facets: string[], group, value): boolean{
        if (facets.length === 0){
            return true;
        }
        return facets.filter(facet => this.matchActiveFacet(facet, group, value)).length === 0;
    }

    matchActiveFacet(facet, group, value){
        const arr = facet.split(',');
        if (arr.length >= 3){
            const valElement = arr.slice(2).join(',');
            if (arr[0] === group && valElement === value){
                return true;
            }
        }
        return false;
    }

    getFacetValueLabel(group: string, key: string, fallbackLabel = null){
        let label = '';
        switch (group) {
            case 'rtype':
                label = this.getTranslationWithFallback('research.facets.facet.facet_rtype.', key);
                break;
            case 'tlevel':
                label = this.getTranslationWithFallback('research.facets.facet.facet_tlevel.', key);
                break;
            case 'lang':
                label = this.getTranslationWithFallback('research.facets.facet.facet_lang.', key);
                break;
            case 'member_organizations_facet':
                this.addTranslationLabel(key, fallbackLabel);
                label = this.getTranslationWithFallback("", key, fallbackLabel)
                break;
            case 'researcher_internal_current_affiliation_code':
            case 'researcher_affiliation_code_facet':
            case 'engagement_type_code_facet':
            case 'position_and_title_facet':
            case 'current_position_and_title_facet':
            case 'internal_position_and_title_facet':
            case 'academic_unit':
            case 'academic_units_facet':
            case 'repository_status':
            case 'status':
            case 'type':
            case 'degree_awarded':
            case 'degree_in':
            case 'project_type':
            case 'resource_subtype':
            case 'attributes':
                label = this.getTranslationWithFallback("", key);
                break;
            default:
                label = key;
                break;
        }
        return label;
    }

    private addTranslationLabel(key: string, fallbackLabel) {
        if (key && fallbackLabel) {
            this.translate.set(key, fallbackLabel);
        }
    }

    getTranslationWithFallback(prefix: string, code: string, fallbackLabel?: string){
        let label = this.translate.instant(prefix + code);
        if (!label){
            label = fallbackLabel ?? code;
        }
        return label;
    }

    processFacetsInclude(facets){
        facets = this.getFacetsAsArray(facets);
        return facets.filter(facet => facet.indexOf(',include,') !== -1).join(Discovery.FACET_JOINER).replace(/,include,/g,',exact,');
    }


    processFacetsExclude(facets){
        facets = this.getFacetsAsArray(facets);
        return facets.filter(facet => facet.indexOf(',exclude,') !== -1).join(Discovery.FACET_JOINER).replace(/,exclude,/g,',exact,');
    }

    processMultiFacet(mfacet){
        const mfacetArr: MultiFacet[] = [];
        mfacet = this.getFacetsAsArray(mfacet);
        mfacet.forEach(mfacetStr =>{
            const group = parseInt(mfacetStr.slice(-1));
            if (!isNaN(group)){
                const multiFacet = new MultiFacet(mfacetStr.substring(0, mfacetStr.length - 2), group);
                mfacetArr.push(multiFacet);
            }
        });

        const mFacetForSearch: string[] = [];
        if (mfacetArr.length > 0){
            for (let i = 1; i <= mfacetArr[mfacetArr.length - 1].multiFacetGroupId; i++){
                const a = mfacetArr.filter(facet => facet.multiFacetGroupId === i).map(facet =>{
                    return facet.multiFacet;
                }).join(Discovery.FACET_JOINER);
                if (a){
                    mFacetForSearch.push(a);
                }
            }
        }
        return mFacetForSearch;
    }

    getFacetsAsArray(facets){
        if (!Array.isArray(facets)){
            facets = [facets];
        }
        return facets;
    }

    processActiveFacets(facets, mfacets){
        facets = this.getFacetsAsArray(facets);
        mfacets = this.getFacetsAsArray(mfacets);
        const activeFacets = this.getActiveFacetsByType(facets, 'facet');
        const activeMultiFacets = this.getActiveFacetsByType(mfacets, 'mfacet');
        return activeFacets.concat(activeMultiFacets);
    }

    getActiveFacetsByType(facets, type): ActiveFacet[]{
        const activeFacets = [];
        facets.forEach(facet =>{
            const facetComponents = facet.split(',');
            if (facetComponents.length > 2){
                const label = this.getActiveFacetLabel(facetComponents, type);
                const activeFacet = new ActiveFacet(facet, label, facetComponents[1] === 'exclude');
                activeFacets.push(activeFacet);
            }
        });
        return activeFacets;
    }

    getActiveFacetLabel(facetComponents, type){
        let label = '';
        switch (facetComponents[0]) {
            case Discovery.INTERNAL_AFFILIATION_START_DATE_FACET:
            case Discovery.CREATION_DATE_FACET:
                label = facetComponents[2].replace('[', '').replace(']', '').replace(Discovery.FACET_DATE_RANGE_SEPERATOR, ' ⇔ ');
                break;
            default:
                label = this.getFacetValueLabel(facetComponents[0].replace('facet_', ''), this.extractFacetValue(facetComponents, type));
                break;
        }
        return label;
    }

    //the value can contain commas, in mfacet the last argument will be the number group so we need to exclude it
    extractFacetValue(facetComponents, type){
        let value = '';
        if (type === 'facet'){
            value = facetComponents.slice(2).join(',');
        } else if (type === 'mfacet'){
            value = facetComponents.slice(2, facetComponents.length-1).join(',');
        }
        return value;
    }

}
